import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Eye, Check, X, FileText, Download } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface ManagementRequest {
  id: string;
  owner_full_name: string;
  property_type: string;
  property_address: string;
  expected_rental_value: number;
  management_type: string;
  status: string;
  created_at: string;
  owner_email: string;
  owner_phone: string;
  property_area: number;
  owner_id_number: string;
  owner_id_image_url: string;
  detailed_address: string;
  ownership_document_url: string;
  property_images_urls: any;
  property_age: number;
  previous_rental_date: string;
  maintenance_date: string;
  preferred_contract_duration: number;
  additional_details: string;
  admin_notes: string;
}

interface ManagementRequestsProps {
  requests: ManagementRequest[];
  onUpdateRequestStatus: (requestId: string, status: string) => Promise<void>;
}

const ManagementRequests = ({ requests, onUpdateRequestStatus }: ManagementRequestsProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'default';
      case 'rejected': return 'destructive';
      case 'under_review': return 'secondary';
      default: return 'outline';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved': return 'مقبول';
      case 'rejected': return 'مرفوض';
      case 'under_review': return 'قيد المراجعة';
      default: return 'في الانتظار';
    }
  };

  const downloadRequestData = (request: ManagementRequest) => {
    const data = {
      'معرف الطلب': request.id,
      'اسم المالك': request.owner_full_name,
      'البريد الإلكتروني': request.owner_email,
      'رقم الهاتف': request.owner_phone,
      'رقم الهوية': request.owner_id_number,
      'نوع العقار': request.property_type,
      'عنوان العقار': request.property_address,
      'العنوان التفصيلي': request.detailed_address,
      'مساحة العقار': `${request.property_area} متر مربع`,
      'عمر العقار': `${request.property_age} سنة`,
      'القيمة المتوقعة': `${request.expected_rental_value} ريال`,
      'نوع الإدارة': request.management_type,
      'مدة العقد المفضلة': `${request.preferred_contract_duration} شهر`,
      'تاريخ الإيجار السابق': request.previous_rental_date || 'غير محدد',
      'تاريخ الصيانة': request.maintenance_date || 'غير محدد',
      'تفاصيل إضافية': request.additional_details || 'لا توجد',
      'ملاحظات الإدارة': request.admin_notes || 'لا توجد',
      'حالة الطلب': getStatusText(request.status),
      'تاريخ الطلب': new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(request.created_at))
    };

    const content = Object.entries(data)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n');

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `طلب-إدارة-عقار-${request.owner_full_name}-${request.id.slice(0, 8)}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadSingleImage = async (imageUrl: string, ownerName: string, imageNumber: number) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // استخراج امتداد الملف من الرابط
      const extension = imageUrl.split('.').pop()?.split('?')[0] || 'jpg';
      link.download = `صورة-عقار-${ownerName}-${imageNumber}.${extension}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ في تحميل الصورة",
        variant: "destructive"
      });
    }
  };

  const downloadAllImages = async (imageUrls: string[], ownerName: string) => {
    try {
      for (let i = 0; i < imageUrls.length; i++) {
        await downloadSingleImage(imageUrls[i], ownerName, i + 1);
        // انتظار قصير بين التحميلات لتجنب إرباك المتصفح
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      toast({
        title: "تم بنجاح",
        description: `تم تحميل ${imageUrls.length} صورة`,
        variant: "default"
      });
    } catch (error) {
      console.error('Error downloading images:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ في تحميل الصور",
        variant: "destructive"
      });
    }
  };

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          طلبات إدارة العقارات
        </CardTitle>
        <CardDescription>مراجعة وإدارة طلبات أصحاب العقارات لخدمات الإدارة</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">اسم المالك</TableHead>
                <TableHead className="text-right">نوع العقار</TableHead>
                <TableHead className="text-right">العنوان</TableHead>
                <TableHead className="text-right">القيمة المتوقعة</TableHead>
                <TableHead className="text-right">نوع الإدارة</TableHead>
                <TableHead className="text-right">الحالة</TableHead>
                <TableHead className="text-right">تاريخ الطلب</TableHead>
                <TableHead className="text-right">الإجراءات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id} className="hover:bg-muted/50">
                  <TableCell className="text-right font-medium">{request.owner_full_name}</TableCell>
                  <TableCell className="text-right">{request.property_type}</TableCell>
                  <TableCell className="text-right">{request.property_address}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline">{request.expected_rental_value} ريال</Badge>
                  </TableCell>
                  <TableCell className="text-right">{request.management_type}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant={getStatusColor(request.status)}>
                      {getStatusText(request.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {new Intl.DateTimeFormat('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    }).format(new Date(request.created_at))}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center gap-2">
                      {/* View Details */}
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline" className="hover:bg-info hover:text-info-foreground">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                              <div className="flex items-center justify-between">
                                <DialogTitle>تفاصيل طلب إدارة العقار</DialogTitle>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => downloadRequestData(request)}
                                  className="flex items-center gap-2"
                                >
                                  <Download className="h-4 w-4" />
                                  تحميل البيانات
                                </Button>
                              </div>
                            </DialogHeader>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div className="space-y-4">
                                <h4 className="font-semibold text-lg border-b pb-2">معلومات المالك</h4>
                                <div className="space-y-3">
                                  <div className="bg-muted/30 p-3 rounded-lg">
                                    <p><strong>الاسم الكامل:</strong> {request.owner_full_name}</p>
                                    <p><strong>البريد الإلكتروني:</strong> {request.owner_email}</p>
                                    <p><strong>رقم الهاتف:</strong> {request.owner_phone}</p>
                                    <p><strong>رقم الهوية:</strong> {request.owner_id_number}</p>
                                  </div>
                                  {request.owner_id_image_url && (
                                    <div>
                                      <p className="font-medium mb-2">صورة الهوية:</p>
                                      <img 
                                        src={request.owner_id_image_url} 
                                        alt="صورة الهوية" 
                                        className="max-w-xs rounded-lg border"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="space-y-4">
                                <h4 className="font-semibold text-lg border-b pb-2">معلومات العقار</h4>
                                <div className="space-y-3">
                                  <div className="bg-muted/30 p-3 rounded-lg">
                                    <p><strong>نوع العقار:</strong> {request.property_type}</p>
                                    <p><strong>العنوان:</strong> {request.property_address}</p>
                                    <p><strong>العنوان التفصيلي:</strong> {request.detailed_address}</p>
                                    <p><strong>المساحة:</strong> {request.property_area} متر مربع</p>
                                    <p><strong>عمر العقار:</strong> {request.property_age} سنة</p>
                                    <p><strong>القيمة الإيجارية المتوقعة:</strong> {request.expected_rental_value} ريال</p>
                                  </div>
                                </div>
                              </div>

                              <div className="space-y-4">
                                <h4 className="font-semibold text-lg border-b pb-2">تفاصيل الإدارة</h4>
                                <div className="space-y-3">
                                  <div className="bg-muted/30 p-3 rounded-lg">
                                    <p><strong>نوع الإدارة:</strong> {request.management_type}</p>
                                    <p><strong>مدة العقد المفضلة:</strong> {request.preferred_contract_duration} شهر</p>
                                    <p><strong>تاريخ الإيجار السابق:</strong> {request.previous_rental_date || 'غير محدد'}</p>
                                    <p><strong>تاريخ آخر صيانة:</strong> {request.maintenance_date || 'غير محدد'}</p>
                                  </div>
                                </div>
                              </div>

                              <div className="space-y-4">
                                <h4 className="font-semibold text-lg border-b pb-2">الحالة والملاحظات</h4>
                                <div className="space-y-3">
                                  <div className="bg-muted/30 p-3 rounded-lg">
                                    <p><strong>حالة الطلب:</strong> 
                                      <Badge variant={getStatusColor(request.status)} className="mr-2">
                                        {getStatusText(request.status)}
                                      </Badge>
                                    </p>
                                    <p><strong>تاريخ الطلب:</strong> {new Intl.DateTimeFormat('en-US', {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    }).format(new Date(request.created_at))}</p>
                                  </div>
                                  {request.additional_details && (
                                    <div>
                                      <p className="font-medium mb-2">تفاصيل إضافية:</p>
                                      <div className="bg-blue-50 p-3 rounded-lg">
                                        <p className="text-sm">{request.additional_details}</p>
                                      </div>
                                    </div>
                                  )}
                                  {request.admin_notes && (
                                    <div>
                                      <p className="font-medium mb-2">ملاحظات الإدارة:</p>
                                      <div className="bg-yellow-50 p-3 rounded-lg border-l-4 border-yellow-400">
                                        <p className="text-sm">{request.admin_notes}</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {request.ownership_document_url && (
                                <div className="space-y-4 md:col-span-2">
                                  <h4 className="font-semibold text-lg border-b pb-2">وثائق الملكية</h4>
                                  <div className="bg-muted/30 p-3 rounded-lg">
                                    <a 
                                      href={request.ownership_document_url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-800 underline flex items-center gap-2"
                                    >
                                      <FileText className="h-4 w-4" />
                                      عرض وثيقة الملكية
                                    </a>
                                  </div>
                                </div>
                              )}

                              {request.property_images_urls && Array.isArray(request.property_images_urls) && request.property_images_urls.length > 0 && (
                                <div className="space-y-4 md:col-span-2">
                                  <div className="flex items-center justify-between">
                                    <h4 className="font-semibold text-lg border-b pb-2">صور العقار</h4>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => downloadAllImages(request.property_images_urls, request.owner_full_name)}
                                      className="flex items-center gap-2"
                                    >
                                      <Download className="h-4 w-4" />
                                      تحميل جميع الصور
                                    </Button>
                                  </div>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {request.property_images_urls.map((imageUrl: string, index: number) => (
                                      <div key={index} className="relative group">
                                        <img 
                                          src={imageUrl} 
                                          alt={`صورة العقار ${index + 1}`} 
                                          className="w-full h-32 object-cover rounded-lg border"
                                        />
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => downloadSingleImage(imageUrl, request.owner_full_name, index + 1)}
                                            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white"
                                          >
                                            <Download className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </DialogContent>
                      </Dialog>
                      
                      {/* Approve/Reject Actions */}
                      {request.status === 'pending' && (
                        <>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => onUpdateRequestStatus(request.id, 'approved')}
                            className="hover:bg-success hover:text-success-foreground"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => onUpdateRequestStatus(request.id, 'rejected')}
                            className="hover:bg-destructive hover:text-destructive-foreground"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {requests.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            لا توجد طلبات إدارة عقارات
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ManagementRequests;